import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) {
    navigate("/dashboard", { replace: true });
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/dashboard");
    } catch (err) {
      // Supabase auth errors have no .response (they're not axios errors);
      // only fall back to formatApiErrorDetail for our own backend responses.
      toast.error(err.response ? formatApiErrorDetail(err.response.data?.detail) : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden overflow-hidden bg-brand lg:block">
        <img
          src="https://images.unsplash.com/photo-1707823942892-3316eeb091a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc4ODU0MzM4MHww&ixlib=rb-4.1.0&q=85"
          alt="Sede aziendale"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center bg-white text-brand">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <span className="font-display text-lg font-extrabold tracking-tight">CHECK·IN</span>
          </div>
          <div>
            <h1 className="font-display text-5xl font-black leading-[1.05] tracking-tight">
              Accoglienza<br />intelligente per<br />ogni sede.
            </h1>
            <p className="mt-6 max-w-md text-white/70">
              Gestisci le tue sedi e le tue policy in modo semplice ed efficiente.
            </p>
          </div>
          <p className="text-xs uppercase tracking-widest text-white/50">Powered by Gaia</p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center bg-canvas px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="font-display text-3xl font-black tracking-tight text-ink">Bentornato</h2>
            <p className="mt-2 text-sm text-slate-500">Accedi al pannello di gestione.</p>
          </div>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
